import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { 
  ArrowLeft, 
  PiggyBank, 
  TrendingUp, 
  Sparkles,
  ChevronRight,
  ChevronDown,
  Leaf,
  Building2,
  Globe,
  Shield,
  Target,
  Repeat,
  BarChart3,
  ArrowDownToLine
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import NavBar from "../components/NavBar";
import EmptyState, { TrustBadge, SecurityNote } from "../components/EmptyState";

const Grow = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("savings");
  const [showSellDialog, setShowSellDialog] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [sellAmount, setSellAmount] = useState("");
  const [showAllInvestments, setShowAllInvestments] = useState(false);

  const { data: smartPot } = useQuery({
    queryKey: ["smartPot"],
    queryFn: async () => {
      const response = await axios.get(`${API}/smart-pot`);
      return response.data;
    }
  });

  const { data: portfolio } = useQuery({
    queryKey: ["portfolio"],
    queryFn: async () => {
      const response = await axios.get(`${API}/investments/portfolio`);
      return response.data;
    }
  });

  const { data: availableInvestments } = useQuery({
    queryKey: ["availableInvestments"],
    queryFn: async () => {
      const response = await axios.get(`${API}/investments/available`);
      return response.data;
    }
  });

  const updateRuleMutation = useMutation({
    mutationFn: async (data) => {
      await axios.put(`${API}/smart-pot/rule`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["smartPot"]);
      toast.success("Savings rule updated!");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to update rule");
    }
  });

  const buyMutation = useMutation({
    mutationFn: async ({ symbol, amount }) => {
      const response = await axios.post(`${API}/investments/buy?symbol=${symbol}&amount=${amount}`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["portfolio"]);
      queryClient.invalidateQueries(["wallets"]);
      toast.success(data.message);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Purchase failed");
    }
  });

  const sellMutation = useMutation({
    mutationFn: async ({ symbol, amount }) => {
      const response = await axios.post(`${API}/investments/sell?symbol=${symbol}&amount=${amount}`);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["portfolio"]);
      queryClient.invalidateQueries(["wallets"]);
      toast.success(`Withdrew $${data.proceeds.toFixed(2)} to your USD wallet`);
      setShowSellDialog(false);
      setSelectedHolding(null);
      setSellAmount("");
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Withdrawal failed");
    }
  });

  const handleSellClick = (holding) => {
    setSelectedHolding(holding);
    setSellAmount("");
    setShowSellDialog(true);
  };

  const handleSellSubmit = () => {
    if (!selectedHolding) return;
    const amount = parseFloat(sellAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (amount > selectedHolding.total_invested) {
      toast.error("Amount exceeds your holdings");
      return;
    }
    sellMutation.mutate({ symbol: selectedHolding.symbol, amount });
  };

  const ruleTypes = [
    { id: "nearest_100_jmd", label: "Round to J$100", description: "Round up every transaction to nearest J$100" },
    { id: "nearest_dollar", label: "Round to $1", description: "Round up to the nearest whole dollar" },
    { id: "percentage", label: "2% of spending", description: "Save 2% of every transaction" }
  ];

  // Portfolio chart colors
  const chartColors = [
    "#14b8a6", // teal
    "#8b5cf6", // violet
    "#f59e0b", // amber
    "#ec4899", // pink
    "#3b82f6", // blue
    "#10b981", // emerald
  ];

  // Calculate portfolio total and percentages for donut chart
  const portfolioTotal = useMemo(() => {
    return portfolio?.reduce((sum, p) => sum + (p.total_invested || 0), 0) || 0;
  }, [portfolio]);

  const portfolioWithPercentages = useMemo(() => {
    if (!portfolio || portfolio.length === 0) return [];
    return portfolio.map((holding, index) => ({
      ...holding,
      percentage: portfolioTotal > 0 ? (holding.total_invested / portfolioTotal) * 100 : 0,
      color: chartColors[index % chartColors.length]
    }));
  }, [portfolio, portfolioTotal]);

  // Generate SVG path for donut chart segments
  const generateDonutSegments = () => {
    if (!portfolioWithPercentages.length) return null;
    
    let cumulativePercent = 0;
    const segments = [];
    const radius = 45;
    const circumference = 2 * Math.PI * radius;
    
    portfolioWithPercentages.forEach((holding, index) => {
      const strokeDasharray = `${(holding.percentage / 100) * circumference} ${circumference}`;
      const strokeDashoffset = -((cumulativePercent / 100) * circumference);
      
      segments.push(
        <circle
          key={holding.symbol}
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={holding.color}
          strokeWidth="10"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 50 50)"
          className="transition-all duration-500"
        />
      );
      
      cumulativePercent += holding.percentage;
    });
    
    return segments;
  };

  // Investments to display (3 or all)
  const displayedInvestments = useMemo(() => {
    if (!availableInvestments) return [];
    return showAllInvestments ? availableInvestments : availableInvestments.slice(0, 3);
  }, [availableInvestments, showAllInvestments]);

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="grow-page">
      <header className="px-6 py-4 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          className="text-foreground"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl">Grow Your Wealth</h1>
      </header>

      {/* Tab Navigation */}
      <div className="px-6 mb-6">
        <div className="flex gap-2 p-1 bg-secondary/50 rounded-xl">
          <button
            onClick={() => setActiveTab("savings")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "savings"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="savings-tab"
          >
            Smart Savings
          </button>
          <button
            onClick={() => setActiveTab("invest")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "invest"
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid="invest-tab"
          >
            Investments
          </button>
        </div>
      </div>

      <main className="px-6">
        {/* Smart Savings Tab */}
        {activeTab === "savings" && (
          <div className="animate-slide-up space-y-6">
            {/* Premium Features - Only on Savings Tab */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Premium Features</p>
              <div className="flex gap-3">
                {/* Recurring Payments Card */}
                <button
                  onClick={() => navigate("/recurring")}
                  data-testid="recurring-payments-card"
                  className="flex-1 glass rounded-2xl p-4 flex flex-col items-start gap-2 
                    transition-all hover:bg-white/10 active:scale-[0.98] text-left min-h-[100px]"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Repeat className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-sm font-semibold text-white leading-tight">
                    Recurring Payments
                  </p>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Set up automatic transfers
                  </p>
                </button>

                {/* Analytics Card */}
                <button
                  onClick={() => navigate("/analytics")}
                  data-testid="analytics-card"
                  className="flex-1 glass rounded-2xl p-4 flex flex-col items-start gap-2 
                    transition-all hover:bg-white/10 active:scale-[0.98] text-left min-h-[100px]"
                >
                  <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-violet-400" />
                  </div>
                  <p className="text-sm font-semibold text-white leading-tight">
                    Analytics
                  </p>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Track your spending habits
                  </p>
                </button>
              </div>
            </div>

            {/* Smart Pot Balance */}
            <div className="glass rounded-3xl p-6 neon-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <PiggyBank className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/70">
                    Smart Pot Balance
                  </p>
                  <h2 className="font-mono-numbers text-3xl font-bold text-foreground">
                    J${smartPot?.balance?.toLocaleString() || "0.00"}
                  </h2>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <p className="text-xs text-muted-foreground">Total Saved</p>
                  <p className="font-mono-numbers text-lg text-primary">
                    J${smartPot?.total_saved?.toLocaleString() || "0.00"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm text-primary font-medium">AI-Powered</span>
                </div>
              </div>
            </div>

            {/* Round-Up Rules */}
            <div>
              <h3 className="font-heading font-medium text-lg mb-4">AI Round-Up Rules</h3>
              <div className="space-y-3">
                {ruleTypes.map((rule) => (
                  <div
                    key={rule.id}
                    className={`glass rounded-2xl p-4 cursor-pointer transition-all ${
                      smartPot?.rule_type === rule.id ? "neon-border" : ""
                    }`}
                    onClick={() => updateRuleMutation.mutate({ 
                      rule_type: rule.id, 
                      is_active: true,
                      percentage: rule.id === "percentage" ? 2 : null
                    })}
                    data-testid={`rule-${rule.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-foreground">{rule.label}</p>
                        <p className="text-xs text-muted-foreground mt-1">{rule.description}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        smartPot?.rule_type === rule.id 
                          ? "border-primary bg-primary" 
                          : "border-muted-foreground/30"
                      }`}>
                        {smartPot?.rule_type === rule.id && (
                          <div className="w-2 h-2 rounded-full bg-primary-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Toggle Active */}
            <div className="glass rounded-2xl p-4 flex items-center justify-between">
              <div>
                <p className="font-medium text-foreground">Auto Round-Up</p>
                <p className="text-xs text-muted-foreground">Save on every transaction</p>
              </div>
              <Switch
                checked={smartPot?.is_active || false}
                onCheckedChange={(checked) => 
                  updateRuleMutation.mutate({ 
                    rule_type: smartPot?.rule_type || "nearest_100_jmd", 
                    is_active: checked 
                  })
                }
                data-testid="roundup-toggle"
              />
            </div>
          </div>
        )}

        {/* Investments Tab */}
        {activeTab === "invest" && (
          <div className="animate-slide-up space-y-6">
            {/* Portfolio Summary with Donut Chart */}
            <div className="glass rounded-3xl p-6">
              <div className="flex items-center justify-between">
                {/* Left side - Info */}
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground/70 mb-1">
                    Portfolio Value
                  </p>
                  <h2 className="font-mono-numbers text-3xl font-bold text-foreground">
                    US${portfolioTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-2">
                    Sandbox Mode • USD
                  </p>
                </div>
                
                {/* Right side - Donut Chart */}
                {portfolio && portfolio.length > 0 ? (
                  <div className="relative w-28 h-28">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      {/* Background circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth="10"
                      />
                      {/* Donut segments */}
                      {generateDonutSegments()}
                    </svg>
                    {/* Center text */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xs text-muted-foreground">{portfolio?.length || 0}</span>
                      <span className="text-[10px] text-muted-foreground">Holdings</span>
                    </div>
                  </div>
                ) : (
                  <div className="w-28 h-28 rounded-full bg-white/5 flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-muted-foreground/30" />
                  </div>
                )}
              </div>
            </div>

            {/* My Holdings with Legend */}
            {portfolio && portfolio.length > 0 && (
              <div>
                <h3 className="font-heading font-medium text-lg mb-4">My Holdings</h3>
                <div className="space-y-3">
                  {portfolioWithPercentages.map((holding) => (
                    <div key={holding.symbol} className="glass rounded-2xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {/* Color indicator */}
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${holding.color}20` }}
                          >
                            <div 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: holding.color }}
                            />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{holding.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {holding.shares?.toFixed(4)} shares • {holding.percentage.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                        <p className="font-mono-numbers text-foreground">
                          ${holding.total_invested?.toLocaleString()}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full btn-secondary"
                        onClick={() => handleSellClick(holding)}
                        data-testid={`withdraw-${holding.symbol}`}
                      >
                        <ArrowDownToLine className="w-4 h-4 mr-2" />
                        Withdraw Funds
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state when no holdings */}
            {(!portfolio || portfolio.length === 0) && (
              <EmptyState
                icon={Target}
                headline="Start building your portfolio"
                description="Choose from curated options below to begin your investment journey."
                variant="compact"
              />
            )}

            {/* Available Investments - Show 3 with View More */}
            <div>
              <h3 className="font-heading font-medium text-lg mb-3">Explore Investments</h3>
              <TrustBadge
                icon={Shield}
                text="Sandbox mode - practice with simulated trades. No real money involved."
                variant="info"
              />
              <div className="space-y-3 mt-3">
                {displayedInvestments.map((inv) => (
                  <div 
                    key={inv.symbol} 
                    className="glass rounded-2xl p-4 card-interactive"
                    data-testid={`investment-${inv.symbol}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          inv.symbol.includes("NCB") || inv.symbol.includes("JMMB")
                            ? "bg-green-500/10 text-green-400"
                            : "bg-blue-500/10 text-blue-400"
                        }`}>
                          {inv.symbol.includes("NCB") || inv.symbol.includes("JMMB") ? (
                            <Building2 className="w-5 h-5" />
                          ) : (
                            <Globe className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{inv.name}</p>
                          <p className="text-xs text-muted-foreground">{inv.symbol}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono-numbers text-foreground">${inv.price}</p>
                        <p className={`text-xs ${inv.change >= 0 ? "text-green-400" : "text-red-400"}`}>
                          {inv.change >= 0 ? "+" : ""}{inv.change}%
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-white/10 flex gap-2">
                      {[10, 25, 50].map(amt => (
                        <Button
                          key={amt}
                          size="sm"
                          variant="outline"
                          className="flex-1 btn-secondary text-xs"
                          onClick={() => buyMutation.mutate({ symbol: inv.symbol, amount: amt })}
                          disabled={buyMutation.isPending}
                        >
                          ${amt}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* View More / View Less Button */}
              {availableInvestments && availableInvestments.length > 3 && (
                <Button
                  variant="ghost"
                  className="w-full mt-4 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowAllInvestments(!showAllInvestments)}
                  data-testid="view-more-investments"
                >
                  {showAllInvestments ? (
                    <>View Less</>
                  ) : (
                    <>View More ({availableInvestments.length - 3} more)</>
                  )}
                  <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showAllInvestments ? "rotate-180" : ""}`} />
                </Button>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Withdraw/Sell Dialog */}
      <Dialog open={showSellDialog} onOpenChange={setShowSellDialog}>
        <DialogContent className="glass-heavy border-white/[0.08] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Withdraw Investment</DialogTitle>
          </DialogHeader>
          
          {selectedHolding && (
            <div className="space-y-4 mt-2">
              <div className="glass rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center font-mono text-xs">
                      {selectedHolding.symbol}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{selectedHolding.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {selectedHolding.shares?.toFixed(4)} shares
                      </p>
                    </div>
                  </div>
                  <p className="font-mono-numbers text-foreground">
                    ${selectedHolding.total_invested?.toFixed(2)}
                  </p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Amount to Withdraw (USD)
                </label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={sellAmount}
                  onChange={(e) => setSellAmount(e.target.value)}
                  className="bg-white/5 border-white/10"
                  max={selectedHolding.total_invested}
                  min={0}
                  step="0.01"
                  data-testid="withdraw-amount-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Max: ${selectedHolding.total_invested?.toFixed(2)}
                </p>
              </div>

              {/* Quick amount buttons */}
              <div className="flex gap-2">
                {[25, 50, 100].map((pct) => {
                  const amt = (selectedHolding.total_invested * pct / 100).toFixed(2);
                  return (
                    <Button
                      key={pct}
                      variant="outline"
                      size="sm"
                      className="flex-1 btn-secondary text-xs"
                      onClick={() => setSellAmount(amt)}
                    >
                      {pct}%
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1 btn-secondary text-xs"
                  onClick={() => setSellAmount(selectedHolding.total_invested?.toFixed(2))}
                >
                  All
                </Button>
              </div>

              <p className="text-xs text-muted-foreground">
                Funds will be credited to your USD wallet immediately.
              </p>
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowSellDialog(false)}
              className="btn-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSellSubmit}
              disabled={sellMutation.isPending || !sellAmount}
              className="btn-primary"
              data-testid="confirm-withdraw-btn"
            >
              {sellMutation.isPending ? "Processing..." : "Withdraw"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NavBar active="grow" />
    </div>
  );
};

export default Grow;
